import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Stack,
  SelectChangeEvent,
  InputLabel,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import MyLocationIcon from "@mui/icons-material/MyLocation";

import { useLocation, useNavigate } from "react-router-dom";
import { PATH_AUTH, PATH_PROFILE } from "../../routes/path";
import { useTheme } from "@mui/material/styles";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { verificationSchema } from "../../validate/authentication";
import { CustomInput } from "../UIElements/input-verification";
import { apiUpdateSignUpFacility } from "../../api/auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotificationService } from "../notification";
import { specialitySelectType } from "../../enum/authentication";
import SelectInputMultiple from "../UIElements/select-multiple";
import { InputCustomSize } from "../UIElements/input-ui";
import { labelSty } from "../UIElements/styles-input";
import { VerifiedModal } from "./modal";
import { LocationModal } from "../UIElements/location-modal";
import { FacilityDataPropsType } from "../../interface/users";

interface LocationDetails {
  address: string;
  latitude: number;
  longitude: number;
  city?: string;
  state?: string;
}

const VerificationDetails = () => {
  const theme = useTheme();
  // const isMedium = useResponsive("down", "md", "xs");
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotificationService();
  const queryClient = useQueryClient();
  const setupData = location.state as
    | (Partial<FacilityDataPropsType> & {
        mode?: "complete-setup" | "verification";
        password?: string;
      })
    | undefined;
  const isProfileSetupFlow = setupData?.mode === "complete-setup";

  // set up validation
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    resolver: zodResolver(verificationSchema),
  });
  const [specialty, setSpecialty] = useState<string[]>([]);
  const [other_num, setOther_num] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [locationDetails, setLocationDetails] =
    useState<LocationDetails | null>(null);

  React.useEffect(() => {
    if (!setupData) return;

    setValue("reg_no", setupData.reg_no ?? "");
    setValue("address", setupData.address ?? "");
    setValue("city", setupData.city ?? "");
    setValue("state", setupData.state ?? "");
    setValue("contact_name", setupData.contact_name ?? "");
    setValue("contact_job_title", setupData.contact_job_title ?? "");
    setValue("contact_email", setupData.contact_email ?? "");
    setValue("phone_number", setupData.phone_number ?? "");
    setOther_num(setupData.other_registration_number ?? "");

    if (setupData.specialties) {
      setSpecialty(
        setupData.specialties
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
      );
    }

    if (setupData.locationDetails?.address) {
      setLocationDetails({
        address: setupData.locationDetails.address,
        latitude: Number(setupData.locationDetails.latitude) || 0,
        longitude: Number(setupData.locationDetails.longitude) || 0,
        city: setupData.locationDetails.city,
        state: setupData.locationDetails.state,
      });
    }
  }, [setValue, setupData]);

  const handleOpenModal = () => {
    setModalOpen(!modalOpen);
  };

  const handleLocationModalOpen = () => {
    setLocationModalOpen(true);
  };

  const handleLocationModalClose = () => {
    setLocationModalOpen(false);
  };

  const handleLocationSelect = (details: LocationDetails) => {
    setLocationDetails(details);
    // Auto-populate address, city, and state fields
    setValue("address", details.address);
    if (details.city) setValue("city", details.city);
    if (details.state) setValue("state", details.state);
    setLocationModalOpen(false);
    showNotification("Location selected successfully", "success");
  };

  const handleModalSubmit = () => {
    if (isProfileSetupFlow) {
      navigate(PATH_PROFILE.root);
      return;
    }
    navigate(PATH_AUTH.signIn);
  };

  const handleSpecialty = (event: SelectChangeEvent<typeof specialty>) => {
    const {
      target: { value },
    } = event;
    setSpecialty(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  // set up react query mutation
  const mutation = useMutation({
    mutationFn: apiUpdateSignUpFacility,
    onSuccess: (data) => {
      // Handle error
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      //  Handle success
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Setup completed", "success");
        queryClient.invalidateQueries({ queryKey: ["profileInfo"] });
        queryClient.invalidateQueries({ queryKey: ["profileDetails"] });

        if (isProfileSetupFlow) {
          navigate(PATH_PROFILE.root);
          return;
        }

        handleOpenModal();
      }
    },
    onError: (error) => {
      console.error("Verification failed", error);
      // Handle error
      showNotification("Verification failed", "error");
    },
  });

  const handleOnSubmit = async (value: any) => {
    if (specialty.length === 0) {
      showNotification("Please select at least one specialty", "error");
      return;
    }
    if (!locationDetails) {
      showNotification("Please select a location", "error");
      return;
    }

    const data_body = {
      ...value,
      facility_id: setupData?._id,
      email: setupData?.email,
      password: setupData?.password ?? "",
      name: setupData?.name,
      facility_type: setupData?.facility_type ?? setupData?.type,
      facility_size: setupData?.facility_size ?? "",
      specialties: specialty,
      other_registration_number: other_num,
      locationDetails: locationDetails,
    };
    mutation.mutate(data_body);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <VerifiedModal
        open={modalOpen}
        modal_handler={handleOpenModal}
        onSubmit={handleModalSubmit}
      />
      <LocationModal
        open={locationModalOpen}
        onClose={handleLocationModalClose}
        onLocationSelect={handleLocationSelect}
      />
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        <CustomInput
          name="reg_no"
          labelText="CAC Registration Number"
          placeholder="Enter registration number here"
          type="text"
          errors={errors}
          register={register}
        />
        <Stack sx={{ my: 2 }}>
          <InputLabel sx={{ ...labelSty }}>
            Other Registration Number (Optional)
          </InputLabel>
          <InputCustomSize
            size={"medium"}
            placeholder={"Other Registration Number"}
            value={other_num}
            onchange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setOther_num(e.target.value)
            }
            type={"text"}
            styles={{ width: "100%" }}
          />
        </Stack>

        {/* Location Selector */}
        <Stack sx={{ my: 2 }}>
          <InputLabel sx={{ ...labelSty }}>Facility Location</InputLabel>
          <Button
            variant="outlined"
            onClick={handleLocationModalOpen}
            startIcon={<MyLocationIcon />}
            sx={{
              justifyContent: "flex-start",
              textAlign: "left",
              borderRadius: "8px",
              padding: "12px 16px",
              textTransform: "none",
              borderColor: theme.palette.divider,
              color: locationDetails
                ? theme.palette.text.primary
                : theme.palette.text.secondary,
            }}
          >
            {locationDetails
              ? locationDetails.address
              : "Select location on map"}
          </Button>
        </Stack>

        {/* address */}
        <CustomInput
          name="address"
          labelText="Address"
          placeholder="Enter address here"
          type="text"
          errors={errors}
          register={register}
        />
        <CustomInput
          name="city"
          labelText="City"
          placeholder="Enter city here"
          type="text"
          errors={errors}
          register={register}
        />
        <CustomInput
          name="state"
          labelText="State"
          placeholder="Enter state here"
          type="text"
          errors={errors}
          register={register}
        />

        <Stack sx={{ my: 2 }}>
          <SelectInputMultiple
            label={"Specialty offered"}
            value={specialty}
            disabled={false}
            onChange={handleSpecialty}
            options={specialitySelectType}
          />
        </Stack>
        <Typography
          variant="h5"
          sx={{ color: theme.palette.text.primary, my: 3 }}
        >
          Primary person contact
        </Typography>

        <CustomInput
          name="contact_name"
          labelText="Contact Name"
          placeholder="Enter name here"
          type="text"
          errors={errors}
          register={register}
        />
        <CustomInput
          name="contact_job_title"
          labelText="Contact Job Title"
          placeholder="Enter job title here"
          type="text"
          errors={errors}
          register={register}
        />
        <CustomInput
          name="contact_email"
          labelText="Contact Email"
          placeholder="Enter contact email"
          type="email"
          errors={errors}
          register={register}
        />
        <CustomInput
          name="phone_number"
          labelText="Phone Number"
          placeholder="Enter number here"
          type="text"
          errors={errors}
          register={register}
        />
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: 4 }}
        >
          <LoadingButton
            loading={mutation.isPending}
            variant="contained"
            type="submit"
            sx={{
              marginLeft: "auto",
              width: "100%",
              borderRadius: "12px",
              fontWeight: 700,
            }}
          >
            {isProfileSetupFlow ? "Complete Setup" : "Verify"}
          </LoadingButton>
        </Stack>
      </Box>
    </Box>
  );
};

export default VerificationDetails;
